import { useForms } from "../funnel/formQueries";
import { ORDER_KEY } from "../funnel/order";
import {
  candidates,
  infoFormKeys,
  readFieldRefs,
  refOf,
  servedForms,
  writeFieldRefs,
} from "../funnel/recapFields";
import { useWordsOf } from "../funnel/useForm";
import { useSettings } from "../settings/context";

/**
 * LES CHAMPS DU FORMULAIRE À RAPPELER SUR LE RÉCAPITULATIF — une case par champ.
 *
 * La liste vient des formulaires d'informations servis dans le parcours, toutes
 * instances comprises, dans l'ordre où le participant les remplit ; le même champ posé
 * deux fois n'apparaît qu'une fois. Ce qui est coché s'écrit `modèle:clé,…` sous la
 * clé du réglage, et le récapitulatif le relit (voir `funnel/recapFields.ts`).
 *
 * ⚠️ AUCUN FORMULAIRE, ET ON LE DIT. Une liste vide sans un mot se lit comme une panne ;
 * la cause est ailleurs — pas d'étape informations, ou pas de formulaire choisi pour
 * elle — et c'est là qu'on renvoie.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */
export function RecapFields({ settingKey }: { settingKey: string }) {
  const { value, write, saving } = useSettings();
  const forms = useForms();
  const served = servedForms(infoFormKeys(value(ORDER_KEY)), value, forms);
  const words = useWordsOf(served);
  const all = candidates(served);
  const refs = readFieldRefs(value(settingKey));
  const picked = new Set(refs.map(refOf));

  if (all.length === 0) {
    return (
      <p className="rcf__none">
        Aucun champ à proposer : le parcours ne sert pas d'étape informations, ou aucun
        formulaire ne lui est choisi.
      </p>
    );
  }

  const toggle = (ref: string, model: "U" | "UE" | "C", key: string) => {
    const next = picked.has(ref)
      ? refs.filter((r) => refOf(r) !== ref)
      : [...refs, { model, key }];
    write(settingKey, writeFieldRefs(next));
  };

  return (
    <div className="pick rcf">
      {all.map(({ field, form }) => {
        const ref = refOf(field);
        const label = (words.get(form.id)?.field(field) ?? field.label).trim() || field.key;
        return (
          <button
            type="button"
            key={ref}
            aria-pressed={picked.has(ref)}
            disabled={!saving}
            /* Le nom du formulaire en survol : deux instances peuvent poser des champs
               aux libellés voisins, et la clé technique dit d'où vient chacun. */
            title={`${form.name} · ${field.key}${field.extended ? " (donnée étendue)" : ""}`}
            onClick={() => toggle(ref, field.model, field.key)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
